import { useMemo, useState } from 'react'
import { rerunForecast } from '../api/client'
import ForecastSettings, { type ForecastConfig } from './ForecastSettings'
import { useUIStore } from '../store/uiStore'
import { useForecastStore } from '../store/forecastStore'
import type { ForecastRun, RerunRequest } from '../types'
import './RerunModal.css'

function configFromRun(run: ForecastRun): ForecastConfig {
  // Handle backwards compatibility: if old reasoning_effort exists, use it for both
  const legacyReasoning = run.config?.reasoning_effort as any
  return {
    formatter_model: run.config?.formatter_model || run.config?.models?.formatter || 'gpt-5-nano',
    predictor_model: run.config?.predictor_model || run.config?.models?.predictor || 'gpt-5-nano',
    supervisor_model: run.config?.supervisor_model || run.config?.models?.supervisor || 'gpt-5-mini',
    search_depth: (run.config?.search_depth as any) || 'low',
    predictor_reasoning_effort: (run.config?.predictor_reasoning_effort as any) || legacyReasoning || 'low',
    supervisor_reasoning_effort: (run.config?.supervisor_reasoning_effort as any) || legacyReasoning || 'medium',
    num_agents: run.config?.num_agents || 6,
    agent_configs: run.config?.agent_configs || null,
  }
}

function makeRerunRequest(questionId: string, cfg: ForecastConfig): RerunRequest {
  return {
    question_id: questionId,
    formatter_model: cfg.formatter_model,
    predictor_model: cfg.predictor_model,
    supervisor_model: cfg.supervisor_model,
    search_depth: cfg.search_depth,
    predictor_reasoning_effort: cfg.predictor_reasoning_effort,
    supervisor_reasoning_effort: cfg.supervisor_reasoning_effort,
    num_agents: cfg.num_agents,
    // Critical: reruns must stay linked to the same question (no reformatting)
    skip_formatting: true,
    agent_configs: cfg.agent_configs || undefined,
  }
}

export default function RerunModal({
  questionId,
  activeRun,
}: {
  questionId: string | null | undefined
  activeRun: ForecastRun | null
}) {
  const isOpen = useUIStore((s) => s.modals.rerun)
  const closeModal = useUIStore((s) => s.closeModal)
  const openModal = useUIStore((s) => s.openModal)
  const setSelectedRunId = useUIStore((s) => s.setSelectedRunId)
  const setCurrentJobId = useForecastStore((s) => s.setCurrentJobId)
  const setCurrentRuns = useForecastStore((s) => s.setCurrentRuns)

  const baseConfig = useMemo(() => (activeRun ? configFromRun(activeRun) : null), [activeRun])
  const [mode, setMode] = useState<'same' | 'edit'>('same')
  const [cfg, setCfg] = useState<ForecastConfig | null>(baseConfig)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const canSubmit = Boolean(questionId && activeRun && cfg)

  const handleClose = () => {
    closeModal('rerun')
    setMode('same')
    setCfg(baseConfig)
  }

  const handleOpenSchedule = () => {
    handleClose()
    openModal('scheduleReruns')
  }

  const handleSubmit = async () => {
    if (!canSubmit || !questionId || !activeRun || !cfg) return
    setIsSubmitting(true)
    try {
      const req = makeRerunRequest(questionId, cfg)
      const resp = await rerunForecast(questionId, req)

      // Optimistically add a running run card so UI updates immediately
      const now = new Date().toISOString()
      const optimistic: ForecastRun = {
        job_id: resp.job_id,
        status: 'running',
        created_at: now,
        completed_at: null,
        formatted_question: activeRun.formatted_question,
        config: {
          ...activeRun.config,
          formatter_model: cfg.formatter_model,
          predictor_model: cfg.predictor_model,
          supervisor_model: cfg.supervisor_model,
          search_depth: cfg.search_depth,
          predictor_reasoning_effort: cfg.predictor_reasoning_effort,
          supervisor_reasoning_effort: cfg.supervisor_reasoning_effort,
          num_agents: cfg.num_agents,
          agent_configs: cfg.agent_configs || undefined,
        },
      }

      const store = useForecastStore.getState()
      setCurrentRuns([optimistic, ...store.currentRuns])
      setSelectedRunId(resp.job_id)
      setCurrentJobId(resp.job_id)
      handleClose()
    } catch (e) {
      console.error('Failed to rerun forecast:', e)
      alert(`Failed to re-run forecast: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content rerun-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Re-run forecast</h2>
          <button className="modal-close" onClick={handleClose} type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="rerun-modes">
            <button
              type="button"
              className={`rerun-mode ${mode === 'same' ? 'active' : ''}`}
              onClick={() => {
                setMode('same')
                setCfg(baseConfig)
              }}
              disabled={!activeRun}
            >
              Same specification
            </button>
            <button
              type="button"
              className={`rerun-mode ${mode === 'edit' ? 'active' : ''}`}
              onClick={() => {
                setMode('edit')
                setCfg(baseConfig)
              }}
              disabled={!activeRun}
            >
              Change specification
            </button>
          </div>

          {mode === 'edit' && cfg && (
            <div className="rerun-settings">
              <ForecastSettings config={cfg} onChange={setCfg} />
            </div>
          )}

          <div className="rerun-note">
            Re-runs stay linked to the same forecasting question (formatting is skipped).
          </div>
        </div>

        <div className="modal-footer">
          <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
            <button
              className="rounded-xl px-4 py-2.5 border border-rule bg-paper text-ink font-semibold text-sm shadow-delphi hover:bg-white/40 disabled:opacity-60 disabled:cursor-not-allowed transition"
              onClick={handleOpenSchedule}
              disabled={isSubmitting}
              type="button"
            >
              Schedule reruns
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              type="button"
            >
              {isSubmitting ? 'Re-running…' : 'Re-run forecast'}
            </button>
            <button
              className="rounded-xl px-4 py-2.5 border border-rule bg-paper text-ink font-semibold text-sm shadow-delphi hover:bg-white/40 disabled:opacity-60 disabled:cursor-not-allowed transition"
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}



